import clsx from 'clsx'
import Link from 'next/link'
import { useEffect, type FC } from 'react'
import Image from 'next/image'

import { Text } from '@wartek-id/text-v1'

import { Card } from 'app/KinerjaShared/components/Card'
import {
  Table,
  TableBody,
  TableHeader,
  TableHeaderItem,
  TableRow,
  TableRowItem,
  TableShimmer,
} from 'app/KinerjaShared/components/Table'
import { PENGELOLAAN_KINERJA_RIWAYAT_PATH } from 'app/KinerjaShared/constants/path'
import { RoleLabelShort } from 'app/KinerjaKSPage/utils/profile'
import { useApiGetRiwayatTahunanByYear } from 'api/kinerja/kepala-sekolah/riwayat'
import styles from 'app/KinerjaRiwayat/pages/styles.module.css'
import ButtonSelengkapnya from 'app/KinerjaRiwayat/components/ButtonSelengkapnya'
import { PREFIX_RIWAYAT_GURU } from 'app/KinerjaKSPage/constants'

import type { TFilterItem } from 'app/KinerjaRiwayat/pages/Riwayat'
import type { KinerjaRoleType } from 'types/kinerja'
import type { TRiwayatItem } from 'types/kinerja-riwayat-ks'

type RiwayatTahunanProps = {
  activeFilter: TFilterItem[]
  activeYear: string
  rowHeaderClasses: string
  rowItemClasses: string
  onLoading?: (isLoading: boolean) => void
}

const getUrl = (item: TRiwayatItem) => {
  if (!item?.id) return null

  const isGuru = item?.role === 'GURU'

  return {
    pathname: `${PENGELOLAAN_KINERJA_RIWAYAT_PATH}/tahunan/[riwayatId]`,
    query: {
      riwayatId: `${isGuru ? PREFIX_RIWAYAT_GURU : ''}${item?.id}`,
      npsn: item.npsn,
    },
  }
}

const RiwayatTahunan: FC<RiwayatTahunanProps> = ({
  activeFilter,
  activeYear,
  rowHeaderClasses,
  rowItemClasses,
  onLoading,
}) => {
  const {
    isLoading,
    isSuccess,
    isError,
    data: riwayatData,
  } = useApiGetRiwayatTahunanByYear(
    {
      year: activeYear,
      roles: activeFilter?.includes('ALL')
        ? null
        : (activeFilter as Array<KinerjaRoleType>),
    },
    { enabled: !!activeFilter && !!activeYear }
  )

  useEffect(() => {
    onLoading?.(isLoading)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading])

  const hasData = riwayatData?.data?.length > 0

  return (
    <section className="flex flex-col gap-6">
      <Text variant="heading-lg">Rincian Tahunan</Text>

      <Table
        data-testid="table-riwayat-tahunan"
        className={clsx('!mt-0', styles.tableRiwayat)}
      >
        <TableHeader>
          <TableHeaderItem className={clsx(rowHeaderClasses, 'w-4/12')}>
            Periode
          </TableHeaderItem>
          <TableHeaderItem className={clsx(rowHeaderClasses, 'pl-0 w-4/12')}>
            Satuan Pendidikan
          </TableHeaderItem>
          <TableHeaderItem className={clsx(rowHeaderClasses, 'pl-0 w-2/12')}>
            Jabatan
          </TableHeaderItem>
          <TableHeaderItem className={clsx(rowHeaderClasses, 'pl-0 w-2/12')} />
        </TableHeader>

        <TableBody>
          {/* If loading */}
          {isLoading && <TableShimmer columns={4} />}

          {(isError || (isSuccess && !hasData)) && (
            <TableRow>
              <TableRowItem colSpan={4} className="p-0">
                <Card
                  className={clsx(
                    // Handle container
                    '!rounded !m-0',
                    'bg-white grayscale',

                    // Handle content
                    '!flex !flex-row items-center gap-6 p-6 relative overflow-hidden'
                  )}
                >
                  <div className="flex-none">
                    <Image
                      alt=""
                      src="/illustrations/img-reflection-documents-magnifying-glass.png"
                      width={64}
                      height={64}
                      className="select-none pointer-events-none"
                    />
                  </div>

                  <div className="flex-1">
                    <div className="flex flex-col gap-1 justify-center">
                      <>
                        <Text variant="heading-sm">
                          Predikat Kinerja (Tahunan) belum tersedia
                        </Text>

                        <Text variant="body-sm" color="subdued">
                          Jika sudah tersedia, Predikat Kinerja (Tahunan) akan
                          muncul di halaman ini.
                        </Text>
                      </>
                    </div>
                  </div>

                  <div className="flex-none z-10"></div>
                </Card>
              </TableRowItem>
            </TableRow>
          )}

          {isSuccess && (
            <>
              {riwayatData?.data?.map((item, index) => {
                const url = getUrl(item)

                return (
                  <TableRow
                    key={`table-row-tahunan-${index}`}
                    data-testid={`row-tahunan-${index + 1}`}
                  >
                    {/* Periode */}
                    <TableRowItem className={rowItemClasses}>
                      Tahunan
                    </TableRowItem>

                    {/* Satuan Pendidikan */}
                    <TableRowItem className={clsx(rowItemClasses, 'pl-0')}>
                      {item?.satuanPendidikan}
                    </TableRowItem>

                    {/* Jabatan */}
                    <TableRowItem className={clsx(rowItemClasses, 'pl-0')}>
                      {RoleLabelShort[item?.role?.toUpperCase()]}
                    </TableRowItem>

                    {/* Buttons */}
                    <TableRowItem className={clsx(rowItemClasses, 'pl-0')}>
                      <div className="flex flex-col items-end gap-2">
                        {/* If URL is undefined, due to missing period no & period year, hide the button */}
                        {!!url && item?.accessible ? (
                          <>
                            <Link href={url} passHref>
                              <ButtonSelengkapnya />
                            </Link>
                          </>
                        ) : (
                          <ButtonSelengkapnya disabled />
                        )}
                      </div>
                    </TableRowItem>
                  </TableRow>
                )
              })}
            </>
          )}
        </TableBody>
      </Table>
    </section>
  )
}
export default RiwayatTahunan
